import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, GripVertical } from "lucide-react";

export default function Settings() {
    const [categories, setCategories] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [wishOptions, setWishOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCategories, setSavingCategories] = useState(false);
    const [savingProductTypes, setSavingProductTypes] = useState(false);
    const [savingWishOptions, setSavingWishOptions] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/meta/internal");
            setCategories(data.problem_categories || []);
            setProductTypes(data.product_types || []);
            setWishOptions(data.wish_options || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Categories handlers
    const addCategory = () => setCategories((p) => [...p, { id: `custom_${Date.now()}`, label: "" }]);
    const updateCategory = (i, key, value) =>
        setCategories((p) => p.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)));
    const removeCategory = (i) => setCategories((p) => p.filter((_, idx) => idx !== i));

    const saveCategories = async () => {
        setSavingCategories(true);
        try {
            await api.put("/meta/categories", { categories });
            toast.success("Problemkategorier gemt");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSavingCategories(false);
        }
    };

    // Product types handlers
    const addProductType = () => setProductTypes((p) => [...p, ""]);
    const updateProductType = (i, value) => setProductTypes((p) => p.map((t, idx) => (idx === i ? value : t)));
    const removeProductType = (i) => setProductTypes((p) => p.filter((_, idx) => idx !== i));
    const moveProductType = (i, dir) => {
        setProductTypes((p) => {
            const arr = [...p];
            const j = i + dir;
            if (j < 0 || j >= arr.length) return arr;
            [arr[i], arr[j]] = [arr[j], arr[i]];
            return arr;
        });
    };

    const saveProductTypes = async () => {
        const cleaned = productTypes.map((t) => t.trim()).filter(Boolean);
        if (cleaned.length === 0) {
            toast.error("Tilføj mindst én produkttype");
            return;
        }
        setSavingProductTypes(true);
        try {
            const { data } = await api.put("/meta/product-types", { product_types: cleaned });
            setProductTypes(data.product_types);
            toast.success("Produkttyper gemt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSavingProductTypes(false);
        }
    };

    // Wish options handlers
    const addWishOption = () => setWishOptions((p) => [...p, ""]);
    const updateWishOption = (i, value) => setWishOptions((p) => p.map((t, idx) => (idx === i ? value : t)));
    const removeWishOption = (i) => setWishOptions((p) => p.filter((_, idx) => idx !== i));
    const moveWishOption = (i, dir) => {
        setWishOptions((p) => {
            const arr = [...p];
            const j = i + dir;
            if (j < 0 || j >= arr.length) return arr;
            [arr[i], arr[j]] = [arr[j], arr[i]];
            return arr;
        });
    };

    const saveWishOptions = async () => {
        const cleaned = wishOptions.map((t) => t.trim()).filter(Boolean);
        if (cleaned.length === 0) {
            toast.error("Tilføj mindst én løsning");
            return;
        }
        setSavingWishOptions(true);
        try {
            const { data } = await api.put("/meta/wish-options", { wish_options: cleaned });
            setWishOptions(data.wish_options);
            toast.success("Ønskede løsninger gemt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setSavingWishOptions(false);
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="settings-page">
            <div>
                <h1 className="font-heading text-3xl font-medium text-[#2C2A29]">Indstillinger</h1>
                <p className="text-sm text-[#78736E] mt-1">Tilpas hvad kunden kan vælge i reklamationsformularen</p>
            </div>

            {/* Problem categories */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-lg font-medium text-[#2C2A29]">Problemkategorier</h2>
                        <p className="text-xs text-[#78736E] mt-0.5">Vises i kundens dropdown for problemtype</p>
                    </div>
                    <Button onClick={addCategory} variant="outline" size="sm" className="border-[#E5E1DA]" data-testid="add-category-button">
                        <Plus className="w-4 h-4 mr-1" />Ny
                    </Button>
                </div>
                {loading && <div className="text-sm text-[#78736E]">Indlæser...</div>}
                <div className="space-y-2">
                    {categories.map((c, i) => (
                        <div key={i} className="flex gap-2" data-testid={`category-row-${i}`}>
                            <Input
                                placeholder="ID (uden mellemrum)"
                                value={c.id}
                                onChange={(e) => updateCategory(i, "id", e.target.value.replace(/\s+/g, "_").toLowerCase())}
                                className="max-w-[180px] font-mono text-xs"
                                data-testid={`category-id-${i}`}
                            />
                            <Input
                                placeholder="Vises for kunden"
                                value={c.label}
                                onChange={(e) => updateCategory(i, "label", e.target.value)}
                                data-testid={`category-label-${i}`}
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeCategory(i)} className="text-[#993C3C]" data-testid={`remove-category-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>
                <Button onClick={saveCategories} disabled={savingCategories} className="mt-6 bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-categories-button">
                    Gem problemkategorier
                </Button>
            </div>

            {/* Product types */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-lg font-medium text-[#2C2A29]">Produkttyper / gardintyper</h2>
                        <p className="text-xs text-[#78736E] mt-0.5">Vises i kundens dropdown når de vælger produkt</p>
                    </div>
                    <Button onClick={addProductType} variant="outline" size="sm" className="border-[#E5E1DA]" data-testid="add-product-type-button">
                        <Plus className="w-4 h-4 mr-1" />Ny
                    </Button>
                </div>
                <div className="space-y-2">
                    {productTypes.map((t, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`product-type-row-${i}`}>
                            <div className="flex flex-col">
                                <button
                                    type="button"
                                    onClick={() => moveProductType(i, -1)}
                                    disabled={i === 0}
                                    className="text-[#78736E] hover:text-[#2C2A29] disabled:opacity-30 px-1 leading-none"
                                    aria-label="Flyt op"
                                    data-testid={`move-up-${i}`}
                                >▲</button>
                                <button
                                    type="button"
                                    onClick={() => moveProductType(i, 1)}
                                    disabled={i === productTypes.length - 1}
                                    className="text-[#78736E] hover:text-[#2C2A29] disabled:opacity-30 px-1 leading-none"
                                    aria-label="Flyt ned"
                                    data-testid={`move-down-${i}`}
                                >▼</button>
                            </div>
                            <Input
                                placeholder="f.eks. Rullegardin"
                                value={t}
                                onChange={(e) => updateProductType(i, e.target.value)}
                                data-testid={`product-type-input-${i}`}
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeProductType(i)} className="text-[#993C3C]" data-testid={`remove-product-type-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                    {productTypes.length === 0 && (
                        <div className="text-sm text-[#78736E] italic py-2">Ingen produkttyper – tilføj mindst én</div>
                    )}
                </div>
                <Button onClick={saveProductTypes} disabled={savingProductTypes} className="mt-6 bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-product-types-button">
                    Gem produkttyper
                </Button>
            </div>

            {/* Wish options */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-lg font-medium text-[#2C2A29]">Ønsket løsning</h2>
                        <p className="text-xs text-[#78736E] mt-0.5">Vises i kundens dropdown for hvilken løsning de ønsker</p>
                    </div>
                    <Button onClick={addWishOption} variant="outline" size="sm" className="border-[#E5E1DA]" data-testid="add-wish-option-button">
                        <Plus className="w-4 h-4 mr-1" />Ny
                    </Button>
                </div>
                <div className="space-y-2">
                    {wishOptions.map((t, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`wish-option-row-${i}`}>
                            <div className="flex flex-col">
                                <button
                                    type="button"
                                    onClick={() => moveWishOption(i, -1)}
                                    disabled={i === 0}
                                    className="text-[#78736E] hover:text-[#2C2A29] disabled:opacity-30 px-1 leading-none"
                                    aria-label="Flyt op"
                                    data-testid={`wish-move-up-${i}`}
                                >▲</button>
                                <button
                                    type="button"
                                    onClick={() => moveWishOption(i, 1)}
                                    disabled={i === wishOptions.length - 1}
                                    className="text-[#78736E] hover:text-[#2C2A29] disabled:opacity-30 px-1 leading-none"
                                    aria-label="Flyt ned"
                                    data-testid={`wish-move-down-${i}`}
                                >▼</button>
                            </div>
                            <Input
                                placeholder="f.eks. Reparation"
                                value={t}
                                onChange={(e) => updateWishOption(i, e.target.value)}
                                data-testid={`wish-option-input-${i}`}
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeWishOption(i)} className="text-[#993C3C]" data-testid={`remove-wish-option-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                    {wishOptions.length === 0 && (
                        <div className="text-sm text-[#78736E] italic py-2">Ingen løsninger – tilføj mindst én</div>
                    )}
                </div>
                <Button onClick={saveWishOptions} disabled={savingWishOptions} className="mt-6 bg-[#4A4641] hover:bg-[#2C2A29] text-white" data-testid="save-wish-options-button">
                    Gem ønskede løsninger
                </Button>
            </div>

            {/* Email config (read-only) */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                <h2 className="font-heading text-lg font-medium text-[#2C2A29] mb-1">Email-konfiguration</h2>
                <p className="text-xs text-[#78736E] mb-4">Opsætning sker via miljøvariabler på serveren</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <Label className="text-xs text-[#78736E]">Afsender email</Label>
                        <div className="text-sm text-[#2C2A29] mt-0.5">Konfigureret i .env (SENDER_EMAIL)</div>
                    </div>
                    <div>
                        <Label className="text-xs text-[#78736E]">Intern notifikation</Label>
                        <div className="text-sm text-[#2C2A29] mt-0.5">INTERNAL_NOTIFICATION_EMAIL</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
